import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { InvalidArgumentException } from "../../Global/Base/Exceptions";
import { IDisposable } from "../../Global/Base/IDisposable";
import { EnumUtils } from "../../Global/Base/Utils/EnumUtils";
import { FunctionUtils } from "../../Global/Base/Utils/FunctionUtils";
import { ElementUtils } from "./Helpers/ElementUtils";
import { createElement, registerCustomElement } from "./Utils/CustomElementUtils";
/**
 * Orientace SplitPanelu
 * @includeToDoc
 */
export enum SplitterOrientation {
    /**
     * Panely jsou pod sebou a splitter je na šířku
     */
    vertical,
    /**
     * Panely jsou vedle sebe a splitter je na výšku
     */
    horizontal,
}

/**
 * Stav minimalizace části SplitPanelu
 * @includeToDoc
 */
export enum SplitPanelMinimalizationState {
    /**
     * Žádná část není minimalizovaná
     */
    none,
    /**
     * Obsah je minimalizovaný k začátku (levé nebo horní části), takže je vidět jen pravá nebo dolní část
     */
    beginning,
    /**
     * Obsah je minimalizovaný ke konci (pravé nebo dolní části), takže je vidět jen levá nebo horní část
     */
    end,
}

/**
 * Kontejnerová komponenta umožňující dynamickou uživatelskou změnu rozměrů vnořených komponent. Musí obsahovat právě dvě komponenty.
 * @includeToDoc
 */
export class SplitPanel extends HTMLElement {
    public static readonly is = "tsc-splitpanel";

    private _firstPanel: HTMLElement;
    private _secondPanel: HTMLElement;
    private _splitter: HTMLElement;
    private _splitterMinimize: SplitPanelMinimizeButton;
    private _orientation?: SplitterOrientation;
    private _minSize?: number | null;
    private _splitterPosition?: number;
    private _inverseSubcomponents?: boolean;
    private _controller: SplitterController;
    private _componentsInversed?: boolean;
    private _minimalized: SplitPanelMinimalizationState;
    private _splitterMovedDebounced: Function;
    private _lastSplitterPositionPct?: number;
    private _isAttached: boolean;

    // @ts-ignore // nečteno, ale ponecháno
    private _minimizeListener?: EventListener;

    /**
     * Zda je komponenta připojená v DOMu
     */
    public get isAttached(): boolean {
        return this._isAttached;
    }

    /**
     * Orientace komponent v kontejneru
     * horizontal - komponenty jsou vedle sebe a splitter je na výšku (výchozí)
     * vertical - komponenty jsou pod sebou a splitter je na šířku
     */
    public get orientation(): SplitterOrientation {
        if (this._orientation === undefined) {
            const o = this.getAttribute("orientation");
            if (EnumUtils.hasEnumValue(SplitterOrientation, o)) {
                this._orientation = EnumUtils.getEnumValueFromName(SplitterOrientation, o, false);
            } else {
                this._orientation = SplitterOrientation.horizontal;
            }
        }

        return this._orientation as SplitterOrientation;
    }

    public set orientation(v: SplitterOrientation) {
        if (this._orientation === v)
            return;

        this._orientation = v;
        this.classList.toggle("horizontal", v === SplitterOrientation.horizontal);
        this.classList.toggle("vertical", v === SplitterOrientation.vertical);
        this.setAttribute("orientation", SplitterOrientation[v]);
        this._configure();
    }

    /**
     * Minimální rozměry, pod které nelze zmenšit vnořené komponenty
     * Hodnota null toto nastavení vypíná a je defaultní
     */
    public get minSize(): number | null {
        if (this._minSize === undefined) {
            const s = this.getAttribute("min-size");
            this._minSize = s && !isNaN(+s) ? +s : null;
        }

        return this._minSize;
    }

    public set minSize(v: number | null) {
        if (v !== null && isNaN(v))
            return;
        if (this._minSize === v)
            return;

        this._minSize = v;
        if (v)
            this.setAttribute("min-size", v.toString());
        else
            this.removeAttribute("min-size");
        this._configure();
    }

    /**
     * Výchozí procentuální pozice umístění splitteru
     * Defaultní hodnotou je 50 (50% - stejná velikost obou komponent a splitter mezi nimi)
     */
    public get splitterPosition(): number {
        if (this._splitterPosition === undefined) {
            const s = this.getAttribute("splitter-position");
            this._splitterPosition = s && !isNaN(+s) ? +s : 50;
        }

        return this._splitterPosition;
    }

    public set splitterPosition(v: number) {
        if (isNaN(v) || v < 0 || v > 100)
            return;

        if (this._splitterPosition === v)
            return;

        this._splitterPosition = v;
        if (v)
            this.setAttribute("splitter-position", v.toString());
        else
            this.removeAttribute("splitter-position");
        this._configure();
    }

    /**
      * Současná procentuální pozice umístění splitteru
      */
    public get currentSplitterPosition(): number {
        return this.isAttached ? this._controller.splitterPositionPct : this._splitterPosition;
    }

    public set currentSplitterPosition(v: number) {
        if (this.isAttached)
            this._controller.splitterPositionPct = v;
        else
            this.splitterPosition = v;
    }

    /**
     * Komponenty vložené ve SplitPanelu budou zobrazeny v opačném pořadí. Tzn. druhá přidaná komponenta bude zobrazena jako první.
     */
    public get inverseSubcomponents(): boolean {
        if (this._inverseSubcomponents === undefined) {
            this._inverseSubcomponents = DataConverter.toBoolean(this.getAttribute("inverse-subcomponents"));
        }

        return this._inverseSubcomponents;
    }

    public set inverseSubcomponents(v: boolean) {
        if (this._inverseSubcomponents === v)
            return;

        this._inverseSubcomponents = v;
        this.setAttribute("inverse-subcomponents", this._inverseSubcomponents.toString());
        this._configure();
    }

    /**
     * Stav minimalizace části panelu
     */
    public get minimalized(): SplitPanelMinimalizationState {
        if (this._minimalized === undefined) {
            const m = this.getAttribute("minimalized");
            this._minimalized = m && EnumUtils.getEnumValueFromName(SplitPanelMinimalizationState, m) || SplitPanelMinimalizationState.none;
        }

        return this._minimalized;
    }

    public set minimalized(v: SplitPanelMinimalizationState) {
        if (this._minimalized === v)
            return;

        this._minimalized = v;
        this.setAttribute("minimalized", SplitPanelMinimalizationState[v]);

        if (this._minimalized !== SplitPanelMinimalizationState.none && this._controller)
            this._controller.collapse(this._minimalized);
    }

    protected static get observedAttributes(): string[] {
        return ["orientation", "min-size", "splitter-position", "inverse-subcomponents", "minimalized"];
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add("split-panel", this.orientation === SplitterOrientation.vertical ? "vertical" : "horizontal");
        this.minimalized = SplitPanelMinimalizationState.none;
        this._splitterMovedDebounced = FunctionUtils.debouncedFunction(() => ElementUtils.callEvent(this, "splittermove", undefined, true), 300);
    }

    private _configure(): void {
        if (!this.isAttached) // u nepřipojeného elementu nefunguje minSize, protože se nedá vypočítat současná velikost
            return;

        if (this.inverseSubcomponents && !this._componentsInversed && this._firstPanel && this._secondPanel) {
            this._checkSubcomponentSwitch();
            this._componentsInversed = true;
        }

        //this._firstPanel.style.width = this.orientation === SplitterOrientation.horizontal ? this.splitterPosition + "%" : "100%";
        //this._firstPanel.style.height = this.orientation === SplitterOrientation.horizontal ? "100%" : this.splitterPosition + "%";

        if (this._controller != null)
            this._controller.dispose();

        // spravuje logiku změny velikosti
        this._controller = new SplitterController(this, this._splitter, this._firstPanel, this._secondPanel, this.orientation,
            [this.splitterPosition, 100 - this.splitterPosition], this.minSize != null ? this.minSize : 0,
            (state: SplitPanelMinimalizationState) => this.minimalized = state, () => this._splitterMovedDebounced());
    }

    private _createPanelElement(element: HTMLElement): void {
        if (this._firstPanel && this._secondPanel)
            throw new InvalidArgumentException("[SplitPanel] Count of child elements can be at most 2");

        if (!this._firstPanel) {
            this._firstPanel = element;
            this._firstPanel.classList.add("split-panel-container", "split-panel-container-first");
            return;
        }

        if (!this._secondPanel) {
            this._secondPanel = element;
            this._secondPanel.classList.add("split-panel-container", "split-panel-container-second");

            if (!this._splitter) {
                this._splitter = createElement({ classes: "splitter" });
                this._splitterMinimize = new SplitPanelMinimizeButton();
                this._splitterMinimize.addEventListener("minimize", this._minimizeListener = (e: IMinimizeEvent) => {
                    if (e.detail.style === SplitPanelMinimalizationState.none)
                        return;

                    this.classList.add("transition");
                    this._firstPanel.addEventListener("transitionend", () => this.classList.remove("transition"), { once: true });

                    if ((this._minimalized === SplitPanelMinimalizationState.beginning && e.detail.style === SplitPanelMinimalizationState.end) || // úplně zprava úplně doleva
                        (this._minimalized === SplitPanelMinimalizationState.end && e.detail.style === SplitPanelMinimalizationState.beginning)) { // úplně zleva úplně doprava
                        this._controller.splitterPositionPct = this._lastSplitterPositionPct || 50; // vrátím se do poslední polohy nebo do půlky
                    } else {
                        this._lastSplitterPositionPct = this.currentSplitterPosition;
                        this._controller.collapse(e.detail.style);
                    }

                    this._splitterMovedDebounced();
                });

                this._splitter.appendChild(this._splitterMinimize);
            }
            this._firstPanel.insertAdjacentElement("afterend", this._splitter);
        }
    }

    /**
     * Přehodí pořadí obou panelů
     */
    private _checkSubcomponentSwitch(): void {
        this._secondPanel.remove();
        this._splitter.remove();

        this._firstPanel.insertAdjacentElement("beforebegin", this._secondPanel);
        this._firstPanel.insertAdjacentElement("beforebegin", this._splitter);

        const second = this._secondPanel;
        this._secondPanel = this._firstPanel;
        this._firstPanel = second;
    }

    protected connectedCallback(): void {
        this._isAttached = true;

        if (this.children.length > 0 && !this._firstPanel && !this._secondPanel) {
            this._createPanelElement(this.children[0] as HTMLElement);
            if (this.children.length > 1) {
                this._createPanelElement(this.children[1] as HTMLElement);
            }

            if (this.children.length > 3) // třetí element je splitter
                for (let i = 3; i < this.children.length; ++i) // pokud by tam někdo naskládal víc elementů (nebo by se tam vygenerovaly elementy pro práva), tak se skryjí a ignorují
                    (this.children[i] as HTMLElement).style.display = "none";
        }

        this._configure();
    }

    protected disconnectedCallback(): void {
        this._isAttached = false;

        if (this._controller != null)
            this._controller.dispose();
    }

    protected attributeChangedCallback(attrName: string, _oldVal: string, newVal: string): void {
        switch (attrName) {
            case "orientation":
                this.orientation = SplitterOrientation[newVal as "vertical" | "horizontal"] || SplitterOrientation.vertical;
                break;
            case "min-size":
                this.minSize = +newVal;
                break;
            case "splitter-position":
                this.splitterPosition = +newVal;
                break;
            case "inverse-subcomponents":
                this.inverseSubcomponents = newVal != null && newVal.toLowerCase() === "true";
                break;
            case "minimalized":
                this.minimalized = newVal && EnumUtils.getEnumValueFromName(SplitPanelMinimalizationState, newVal) || SplitPanelMinimalizationState.none;
                break;
        }
    }
}

/**
 * Událost minimalizace subkomponenty SplitPanelu
 */
interface IMinimizeEvent extends CustomEvent {
    detail: IMinimizeEventData;
}

/**
 * Data událost minimalizace subkomponenty SplitPanelu
 */
interface IMinimizeEventData {
    style: SplitPanelMinimalizationState;
}

class SplitPanelMinimizeButton extends HTMLElement {
    public static readonly is = "tsc-splitpanel-minimizebutton";

    private _menu?: HTMLElement;
    private _minimizeBeginningElement?: HTMLElement;
    private _minimizeEndElement?: HTMLElement;

    private _minimizeBeginningListener?: EventListener;
    private _minimizeEndListener?: EventListener;
    private _mouseenterListener?: EventListener;
    private _mouseleaveListener?: EventListener;
    private _cancelEventListener?: EventListener;

    protected static get observedAttributes(): string[] {
        return ["minimalized"];
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add("minimize-button", "icon", "ui-more");
    }

    protected connectedCallback(): void {
        if (this.children.length === 0) {
            this._menu = createElement({ classes: "minimize-menu", css: { opacity: 0, display: "none" } });
            this._minimizeBeginningElement = createElement({ type: "button", classes: ["btn", "flat", "icon", "ui-chevron-top-small"] });
            this._minimizeEndElement = createElement({ type: "button", classes: ["btn", "flat", "icon", "ui-chevron-bottom-small"] });

            this._menu.appendChild(this._minimizeBeginningElement);
            this._menu.appendChild(this._minimizeEndElement);
            this.appendChild(this._menu);
        }

        this._minimizeBeginningElement.addEventListener("click", this._minimizeBeginningListener = () => {
            if (this._minimizeBeginningElement.classList.contains("Disabled"))
                return;
            this._hide(false);
            ElementUtils.callEvent(this, "minimize", { style: SplitPanelMinimalizationState.beginning } as IMinimizeEventData);
        });
        this._minimizeEndElement.addEventListener("click", this._minimizeEndListener = () => {
            if (this._minimizeEndElement.classList.contains("Disabled"))
                return;
            this._hide(false);
            ElementUtils.callEvent(this, "minimize", { style: SplitPanelMinimalizationState.end } as IMinimizeEventData);
        });

        this.addEventListener("mouseenter", this._mouseenterListener = (_e) => {
            this._show();
        });
        this._menu.addEventListener("mouseleave", this._mouseleaveListener = (_e) => {
            this._hide();
        });

        // tažení za šipky nebude nic dělat
        this._cancelEventListener = (e: Event) => e.stopPropagation();
        this._menu.addEventListener("mousedown", this._cancelEventListener);
        this._menu.addEventListener("touchstart", this._cancelEventListener, { passive: false });
    }

    protected disconnectedCallback(): void {
        if (this._mouseenterListener)
            this.removeEventListener("mouseenter", this._mouseenterListener);
        if (this._mouseleaveListener)
            this._menu.removeEventListener("mouseleave", this._mouseleaveListener);
        if (this._minimizeBeginningElement && this._minimizeBeginningListener)
            this._minimizeBeginningElement.removeEventListener("click", this._minimizeBeginningListener);
        if (this._minimizeEndElement && this._minimizeEndListener)
            this._minimizeEndElement.removeEventListener("click", this._minimizeEndListener);
        if (this._cancelEventListener) {
            this._menu.removeEventListener("mousedown", this._cancelEventListener);
            this._menu.removeEventListener("touchstart", this._cancelEventListener);
        }
    }

    private _hide(soft: boolean = true): void {
        if (soft) {
            this._menu.classList.add("transition");
            this._menu.addEventListener("transitionend", () => {
                this._menu.classList.remove("transition");
                if (this._menu.style.opacity === "0")
                    this._menu.style.display = "none";
            }, { once: true });
        }

        this._menu.style.opacity = "0";
    }

    private _show(soft: boolean = true): void {
        if (soft) {
            this._menu.classList.add("transition");
            this._menu.addEventListener("transitionend", () => {
                this._menu.classList.remove("transition");
            }, { once: true });
        }

        this._menu.style.display = "";
        this._menu.style.opacity = "1";
    }
}

/**
 * Logická komponenta ovládající posun splitteru a změnu rozměrů komponent
 */
class SplitterController implements IDisposable {
    private _split: Split | null;
    private _minimalized: SplitPanelMinimalizationState;

    public set splitterPositionPct(position: number) {
        this._split.setSizes([position, 100 - position]);
    }

    public get splitterPositionPct(): number {
        return this._split.getSizes()[0];
    }

    constructor(_splitPanel: HTMLElement, splitter: HTMLElement, element1: HTMLElement, element2: HTMLElement, orientation: SplitterOrientation, sizes: number[],
        minSize?: number, onMinimalization?: (state: SplitPanelMinimalizationState) => void, onDrag?: Function) {
        this._minimalized = SplitPanelMinimalizationState.none;
        this._split = new Split([element1, element2], {
            sizes: sizes,
            gutter: () => splitter,
            gutterSize: 12, // hodnota by měla odpovídat proměnné @gutter z ControlsVarabless.less
            direction: orientation === SplitterOrientation.horizontal ? "horizontal" : "vertical",
            minSize: [minSize, minSize],
            onDrag: () => {
                window.dispatchEvent(new CustomEvent("resizecomponent"));
                if (onDrag)
                    onDrag();
            }, // událost oznamuje změnu rozměrů, na které mohou reagovat další komponenty
            onMinimalization: (state: SplitPanelMinimalizationState) => {
                this._minimalized = state;
                window.dispatchEvent(new CustomEvent("resizecomponent"));
                if (onMinimalization)
                    onMinimalization(state);
            },
        });
    }

    public collapse(minimalizedState: number, respectMinSize: boolean = true): void {
        if (this._minimalized === minimalizedState)
            return;

        this._minimalized = minimalizedState;
        this._split.collapse(this._minimalized === SplitPanelMinimalizationState.beginning ? 0 : 1, respectMinSize);
    }

    public dispose(): void {
        this._split?.destroy();
    }
}

interface ISplitPair {
    a: number;
    b: number;
    size?: number;
    aMin?: number;
    bMin?: number;
    dragging: boolean;
    parent: HTMLElement;
    isFirst: boolean;
    isLast: boolean;
    direction: "horizontal" | "vertical";
    gutter?: HTMLElement;
    aGutterSize?: number;
    bGutterSize?: number;
    gutterListener?: EventListener;
}

/**
 * Split.js - v1.3.5
 * https://github.com/nathancahill/Split.js
 * License
 * Copyright (c) 2017 Nathan Cahill
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
class Split {
    // ReSharper disable InconsistentNaming

    // The programming goals of Split.js are to deliver readable, understandable and
    // maintainable code, while at the same time manually optimizing for tiny minified file size,
    // browser compatibility without additional requirements, graceful fallback (IE8 is supported)
    // and very few assumptions about the user's page layout.
    private NOOP = function() {
        return false;
    };

    private pairs: ISplitPair[];
    private elements: {
        element: any;
        size: number;
        minSize: number | string;
    }[];
    private dimension: "width" | "height";
    private position: "left" | "top";
    private onMinimalization?: (state: SplitPanelMinimalizationState) => void;

    // This library only needs two helper functions:
    //
    // The first determines which prefixes of CSS calc we need.
    // We only need to do this once on startup, when this anonymous function is called.
    //
    // Tests -webkit, -moz and -o prefixes. Modified from StackOverflow:
    // http://stackoverflow.com/questions/16625140/js-feature-detection-to-detect-the-usage-of-webkit-calc-over-calc/16625167#16625167
    private calc = (["", "-webkit-", "-moz-", "-o-"].filter(function(prefix) {
        const el = document.createElement("div");
        el.style.cssText = "width:" + prefix + "calc(9px)";

        return (!!el.style.length);
    }).shift())! + "calc";

    private elementStyle: (dim: any, size: any, gutSize: any) => any;

    // The second helper function allows elements and string selectors to be used
    // interchangeably. In either case an element is returned. This allows us to
    // do `Split([elem1, elem2])` as well as `Split(['#id1', '#id2'])`.
    private elementOrSelector = function(el: any) {
        if (typeof el === "string") {
            return document.querySelector(el);
        }

        return el;
    };

    // The main function to initialize a split. Split.js thinks about each pair
    // of elements as an independant pair. Dragging the gutter between two elements
    // only changes the dimensions of elements in that pair. This is key to understanding
    // how the following functions operate, since each function is bound to a pair.
    //
    // A pair object is shaped like this:
    //
    // {
    //     a: DOM element,
    //     b: DOM element,
    //     aMin: Number,
    //     bMin: Number,
    //     dragging: Boolean,
    //     parent: DOM element,
    //     isFirst: Boolean,
    //     isLast: Boolean,
    //     direction: 'horizontal' | 'vertical'
    // }
    //
    // The basic sequence:
    //
    // 1. Set defaults to something sane. `options` doesn't have to be passed at all.
    // 2. Initialize a bunch of strings based on the direction we're splitting.
    //    A lot of the behavior in the rest of the library is paramatized down to
    //    rely on CSS strings and classes.
    // 3. Define the dragging helper functions, and a few helpers to go with them.
    // 4. Loop through the elements while pairing them off. Every pair gets an
    //    `pair` object, a gutter, and special isFirst/isLast properties.
    // 5. Actually size the pair elements, insert gutters and attach event listeners.
    constructor(ids: HTMLElement[], options: { [key: string]: any } = {}) {
        let clientAxis: any;

        // All DOM elements in the split should have a common parent. We can grab
        // the first elements parent and hope users read the docs because the
        // behavior will be whacky otherwise.
        const parent = this.elementOrSelector(ids[0]).parentNode;
        const parentFlexDirection = getComputedStyle(parent).flexDirection;

        // Set default options.sizes to equal percentages of the parent element.
        const sizes = options.sizes || ids.map(function() {
            return 100 / ids.length;
        });

        // Standardize minSize to an array if it isn't already. This allows minSize
        // to be passed as a number.
        const minSize = options.minSize !== undefined ? options.minSize : 100;
        const minSizes = Array.isArray(minSize) ? minSize : ids.map(function() {
            return minSize;
        });
        const gutterSize = options.gutterSize !== undefined ? options.gutterSize : 10;
        const snapOffset = options.snapOffset !== undefined ? options.snapOffset : 30;
        const direction = options.direction || "horizontal";
        const cursor = options.cursor || (direction === "horizontal" ? "ew-resize" : "ns-resize");
        const gutter = options.gutter || (function(_i: any, gutterDirection: any) {
            const gut = document.createElement("div");
            gut.className = "gutter gutter-" + gutterDirection;
            return gut;
        });
        this.onMinimalization = options.onMinimalization;
        this.elementStyle = options.elementStyle || ((dim: any, size: any, gutSize: any) => {
            const style: { [key: string]: any } = {};

            if (typeof size !== "string") {
                style[dim] = this.calc + "(" + size + "% - " + gutSize + "px)";
            } else {
                style[dim] = size;
            }

            return style;
        });
        const gutterStyle = options.gutterStyle || (function(dim: any, gutSize: any) {
            let obj: { [key: string]: any };
            return ((obj = {}, obj[dim] = (gutSize + "px"), obj));
        });

        // 2. Initialize a bunch of strings based on the direction we're splitting.
        // A lot of the behavior in the rest of the library is paramatized down to
        // rely on CSS strings and classes.
        if (direction === "horizontal") {
            this.dimension = "width";
            clientAxis = "clientX";
            this.position = "left";
        } else if (direction === "vertical") {
            this.dimension = "height";
            clientAxis = "clientY";
            this.position = "top";
        }

        // 3. Define the dragging helper functions, and a few helpers to go with them.
        // Each helper is bound to a pair object that contains it's metadata. This
        // also makes it easy to store references to listeners that that will be
        // added and removed.
        //
        // Even though there are no other functions contained in them, aliasing
        // this to self saves 50 bytes or so since it's used so frequently.
        //
        // The pair object saves metadata like dragging state, position and
        // event listener references.

        const setGutterSize = (gutterElement: any, gutSize: any) => {
            const style = gutterStyle(this.dimension, gutSize);

            // eslint-disable-next-line no-param-reassign
            Object.keys(style).forEach(function(prop) {
                return (gutterElement.style[prop] = style[prop]);
            });
        };

        // drag, where all the magic happens. The logic is really quite simple:
        //
        // 1. Ignore if the pair is not dragging.
        // 2. Get the offset of the event.
        // 3. Snap offset to min if within snappable range (within min + snapOffset).
        // 4. Actually adjust each element in the pair to offset.
        //
        // ---------------------------------------------------------------------
        // |    | <- a.minSize               ||              b.minSize -> |    |
        // |    |  | <- this.snapOffset      ||     this.snapOffset -> |  |    |
        // |    |  |                         ||                        |  |    |
        // |    |  |                         ||                        |  |    |
        // ---------------------------------------------------------------------
        // | <- this.start                                        this.size -> |
        const drag = (pair: any, e: any) => {
            let offset: any;

            if (!pair.dragging) {
                return;
            }

            // Get the offset of the event from the first side of the
            // pair `this.start`. Supports touch events, but not multitouch, so only the first
            // finger `touches[0]` is counted.
            if ("touches" in e) {
                offset = e.touches[0][clientAxis] - pair.start;
            } else {
                offset = e[clientAxis] - pair.start;
            }

            // If within snapOffset of min or max, set offset to min or max.
            // snapOffset buffers a.minSize and b.minSize, so logic is opposite for both.
            // Include the appropriate gutter sizes to prevent overflows.
            if (offset <= this.elements[pair.a].minSize + snapOffset + pair.aGutterSize) {
                offset = this.elements[pair.a].minSize + pair.aGutterSize;
            } else if (offset >= pair.size - (this.elements[pair.b].minSize + snapOffset + pair.bGutterSize)) {
                offset = pair.size - (this.elements[pair.b].minSize + pair.bGutterSize);
            }

            // Actually adjust the size.
            this.adjust(pair, offset);

            // Call the drag callback continously. Don't do anything too intensive
            // in this callback.
            if (options.onDrag) {
                options.onDrag();
            }
        };

        // stopDragging is very similar to startDragging in reverse.
        const stopDragging = (pair: any) => {
            const a = this.elements[pair.a].element;
            const b = this.elements[pair.b].element;

            if (pair.dragging && options.onDragEnd) {
                options.onDragEnd();
            }

            pair.dragging = false;

            // Remove the stored event listeners. This is why we store them.
            removeEventListener("mouseup", pair.stop);
            removeEventListener("touchend", pair.stop);
            removeEventListener("touchcancel", pair.stop);

            removeEventListener("mousemove", pair.move);
            removeEventListener("touchmove", pair.move);

            // Delete them once they are removed. I think this makes a difference
            // in memory usage with a lot of splits on one page. But I don't know for sure.
            delete pair.stop;
            delete pair.move;

            a.removeEventListener("selectstart", this.NOOP);
            a.removeEventListener("dragstart", this.NOOP);
            b.removeEventListener("selectstart", this.NOOP);
            b.removeEventListener("dragstart", this.NOOP);

            a.style.userSelect = "";
            a.style.webkitUserSelect = "";
            a.style.MozUserSelect = "";
            a.style.pointerEvents = "";

            b.style.userSelect = "";
            b.style.webkitUserSelect = "";
            b.style.MozUserSelect = "";
            b.style.pointerEvents = "";

            pair.gutter.style.cursor = "";
            pair.parent.style.cursor = "";
        };

        // startDragging calls `calculateSizes` to store the inital size in the pair object.
        // It also adds event listeners for mouse/touch events,
        // and prevents selection while dragging so avoid the selecting text.
        const startDragging = (pair: any, e: any) => {
            // Alias frequently used variables to save space. 200 bytes.
            const a = this.elements[pair.a].element;
            const b = this.elements[pair.b].element;

            // Call the onDragStart callback.
            if (!pair.dragging && options.onDragStart) {
                options.onDragStart();
            }

            // Don't actually drag the element. We emulate that in the drag function.
            e.preventDefault();

            // Set the dragging property of the pair object.
            pair.dragging = true;

            // Create two event listeners bound to the same pair object and store
            // them in the pair object.
            pair.move = (e: DragEvent) => drag(pair, e);
            pair.stop = () => stopDragging(pair);

            // All the binding. `window` gets the stop events in case we drag out of the elements.
            addEventListener("mouseup", pair.stop);
            addEventListener("touchend", pair.stop);
            addEventListener("touchcancel", pair.stop);

            addEventListener("mousemove", pair.move);
            addEventListener("touchmove", pair.move);

            // Disable selection. Disable!
            a.addEventListener("selectstart", this.NOOP);
            a.addEventListener("dragstart", this.NOOP);
            b.addEventListener("selectstart", this.NOOP);
            b.addEventListener("dragstart", this.NOOP);

            a.style.userSelect = "none";
            a.style.webkitUserSelect = "none";
            a.style.MozUserSelect = "none";
            a.style.pointerEvents = "none";

            b.style.userSelect = "none";
            b.style.webkitUserSelect = "none";
            b.style.MozUserSelect = "none";
            b.style.pointerEvents = "none";

            // Set the cursor, both on the gutter and the parent element.
            // Doing only a, b and gutter causes flickering.
            pair.gutter.style.cursor = cursor;
            pair.parent.style.cursor = cursor;

            // Cache the initial sizes of the pair.
            this.calculateSizes(pair);
        };

        // 5. Create pair and element objects. Each pair has an index reference to
        // elements `a` and `b` of the pair (first and second elements).
        // Loop through the elements while pairing them off. Every pair gets a
        // `pair` object, a gutter, and isFirst/isLast properties.
        //
        // Basic logic:
        //
        // - Starting with the second element `i > 0`, create `pair` objects with
        //   `a = i - 1` and `b = i`
        // - Set gutter sizes based on the _pair_ being first/last. The first and last
        //   pair have gutterSize / 2, since they only have one half gutter, and not two.
        // - Create gutter elements and add event listeners.
        // - Set the size of the elements, minus the gutter sizes.
        //
        // -----------------------------------------------------------------------
        // |     i=0     |         i=1         |        i=2       |      i=3     |
        // |             |       isFirst       |                  |     isLast   |
        // |           pair 0                pair 1             pair 2           |
        // |             |                     |                  |              |
        // -----------------------------------------------------------------------
        this.pairs = [];
        this.elements = ids.map((id, i) => {
            // Create the element object.
            const element = {
                element: this.elementOrSelector(id),
                size: sizes[i],
                minSize: minSizes[i],
            };

            let pair: ISplitPair | null = null;

            if (i > 0) {
                // Create the pair object with it's metadata.
                pair = {
                    a: i - 1,
                    b: i,
                    dragging: false,
                    isFirst: (i === 1),
                    isLast: (i === ids.length - 1),
                    direction: direction,
                    parent: parent,
                } as ISplitPair;

                // For first and last pairs, first and last gutter width is half.
                pair.aGutterSize = gutterSize;
                pair.bGutterSize = gutterSize;

                if (pair.isFirst) {
                    pair.aGutterSize = gutterSize / 2;
                }

                if (pair.isLast) {
                    pair.bGutterSize = gutterSize / 2;
                }

                // if the parent has a reverse flex-direction, switch the pair elements.
                if (parentFlexDirection === "row-reverse" || parentFlexDirection === "column-reverse") {
                    const temp = pair.a;
                    pair.a = pair.b;
                    pair.b = temp;
                }

                // Determine the size of the current element. IE8 is supported by
                // staticly assigning sizes without draggable gutters. Assigns a string
                // to `size`.
                //
                // IE9 and above
                // Create gutter elements for each pair.

                const gutterElement = gutter(i, direction);
                setGutterSize(gutterElement, gutterSize);

                pair.gutterListener = (e: MouseEvent) => startDragging(pair, e);

                gutterElement.addEventListener("mousedown", pair.gutterListener);
                gutterElement.addEventListener("touchstart", pair.gutterListener);

                parent.insertBefore(gutterElement, element.element);

                pair.gutter = gutterElement;
            }

            // Set the element size to our determined size.
            // Half-size gutters for first and last elements.
            if (i === 0 || i === ids.length - 1) {
                this.setElementSize(element.element, element.size, gutterSize / 2);
            } else {
                this.setElementSize(element.element, element.size, gutterSize);
            }

            const computedSize = element.element.getBoundingClientRect()[this.dimension];

            if (computedSize > 0 && computedSize < element.minSize) {
                element.minSize = computedSize;
            }

            // After the first iteration, and we have a pair object, append it to the
            // list of pairs.
            if (i > 0 && pair) {
                this.pairs.push(pair);

                if (this.onMinimalization) {
                    if (element.size === 100) // velikost druhého elementu je 100%
                        this.onMinimalization(SplitPanelMinimalizationState.beginning);
                    else if (element.size === 0)// velikost druhého elementu je 0%
                        this.onMinimalization(SplitPanelMinimalizationState.end);
                }
            }

            return element;
        });
    }

    private calculateSizes(pair: any): void {
        const a = this.elements[pair.a].element;
        const b = this.elements[pair.b].element;
        pair.size = a.getBoundingClientRect()[this.dimension] + b.getBoundingClientRect()[this.dimension] + pair.aGutterSize + pair.bGutterSize;
        pair.start = a.getBoundingClientRect()[this.position];
    }

    // Actually adjust the size of elements `a` and `b` to `offset` while dragging.
    // calc is used to allow calc(percentage + gutterpx) on the whole split instance,
    // which allows the viewport to be resized without additional logic.
    // Element a's size is the same as offset. b's size is total size - a size.
    // Both sizes are calculated from the initial parent percentage,
    // then the gutter size is subtracted.
    private adjust(pair: any, offset: number): void {
        const a = this.elements[pair.a];
        const b = this.elements[pair.b];
        const percentage = a.size + b.size;

        a.size = (offset / pair.size) * percentage;
        b.size = (percentage - ((offset / pair.size) * percentage));

        this.setElementSize(a.element, a.size, pair.aGutterSize);
        this.setElementSize(b.element, b.size, pair.bGutterSize);

        this._triggerMinimalizationChanged(pair, offset);
    }

    private setElementSize(el: any, size: any, gutSize: any): void {
        // Split.js allows setting sizes via numbers (ideally), or if you must,
        // by string, like '300px'. This is less than ideal, because it breaks
        // the fluid layout that `calc(% - px)` provides. You're on your own if you do that,
        // make sure you calculate the gutter size by hand.
        const style = this.elementStyle(this.dimension, size, gutSize);

        // eslint-disable-next-line no-param-reassign
        Object.keys(style).forEach((prop) => {
            return (el.style[prop] = style[prop]);
        });
    }

    public setSizes(newSizes: any[]): void {
        newSizes.forEach((newSize: any, i: any) => {
            if (i > 0) {
                const pair = this.pairs[i - 1];
                const a = this.elements[pair.a];
                const b = this.elements[pair.b];

                this.calculateSizes(pair); // při prvním volání nemusí být vypočítaná velikost elementů

                a.size = newSizes[i - 1];
                b.size = newSize;

                this.setElementSize(a.element, a.size, pair.aGutterSize);
                this.setElementSize(b.element, b.size, pair.bGutterSize);

                this._triggerMinimalizationChanged(pair, pair.size * a.size / 100);
            }
        });
    }

    /**
     * Vypočítá v jakém stavu (minimalizovaný vlevo/vpravo/neminimalizovaný) teď splitter je a zavolá callback při jeho změně
     * @param pair Dvojice elementů ve SplitPanelu spolu s daty
     * @param offset Posunutí splitteru zleva (pixely)
     */
    private _triggerMinimalizationChanged(pair: any, offset: number): void {
        if (!this.onMinimalization)
            return;

        if (Math.floor(offset) <= this.elements[pair.a].minSize + pair.aGutterSize + 1) // velikost prvního elementu je menší než minimální velikost
            this.onMinimalization(SplitPanelMinimalizationState.beginning);
        else if (Math.floor(pair.size) - Math.floor(offset) - pair.bGutterSize - 1 <= this.elements[pair.b].minSize) // velikost posledního elementu je menší než minimální velikost
            this.onMinimalization(SplitPanelMinimalizationState.end);
        else
            this.onMinimalization(SplitPanelMinimalizationState.none);
    }

    public destroy(): void {
        this.pairs.forEach((pair) => {
            pair.parent.removeChild(pair.gutter);
            pair.gutter.style[this.dimension] = "";
            pair.gutter.removeEventListener("mousedown", pair.gutterListener);
            pair.gutter.removeEventListener("touchstart", pair.gutterListener);
            this.elements[pair.a].element.style[this.dimension] = "";
            this.elements[pair.b].element.style[this.dimension] = "";
        });
    }

    public getSizes(): any[] {
        return this.elements.map(function(element: any) {
            return element.size;
        });
    }

    public collapse(indexOfCollapsedPair: number, respectMinSize: boolean): void {
        if (indexOfCollapsedPair === this.pairs.length) { // poslední se zavře doprava/dolu
            const pair = this.pairs[indexOfCollapsedPair - 1];

            this.calculateSizes(pair);
            let size = pair.size - pair.bGutterSize;
            if (respectMinSize) {
                const minSize = this.elements[pair.b].minSize;
                if (typeof minSize === "number")
                    size -= minSize;
            }
            this.adjust(pair, size);
        } else if (indexOfCollapsedPair < this.pairs.length) { // ostatní doleva/nahoru
            const pair = this.pairs[indexOfCollapsedPair];

            this.calculateSizes(pair);
            let size = pair.bGutterSize;
            if (respectMinSize) {
                const minSize = this.elements[pair.a].minSize;
                if (typeof minSize === "number")
                    size += minSize;
            }
            this.adjust(pair, size);
        }
    }
    // ReSharper restore InconsistentNaming
}

registerCustomElement(SplitPanelMinimizeButton.is, SplitPanelMinimizeButton);
registerCustomElement(SplitPanel.is, SplitPanel);
